import React, { useState, useEffect, useContext } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import Checkbox from "../components/common/checkbox";
import Select from "../components/common/select";
import http from "../services/http";
import { Link } from "react-router-dom";
import UserContext from "../context/userContext";
import DeleteModal from "../components/common/DeleteModal";

const CCFList = (props) => {
  const [ccfs, setCcfs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [parameters, setParameters] = useState([]);
  const [parameter, setParameter] = useState("");
  const [operations, setOperations] = useState([]);
  const [ccfOperations, setCcfOperations] = useState([]);
  const [operation, setOperation] = useState("");
  const [commentTypes, setCommentTypes] = useState([]);
  const [commentType, setCommentType] = useState('');
  const [comment, setComment] = useState("");
  const [showClosedCCFs, setShowClosedCCFs] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteHeader, setDeleteHeader] = useState();
  const [deleteItem, setDeleteItem] = useState();
  const [commentCount, setCommentCount] = useState();

  const [user] = useContext(UserContext);
  
  useEffect(() => {
    async function getCCFs() {
      const siteQuery = user.SITE !== "CO" ? `?site=${user.SITE}` : ``;

      try {
        const { data } = await http.get(`/ccf${siteQuery}`);
        setCcfs(data);
      }
      catch {
        //ignore error
      }
    }

    if (user) {
      getCCFs();
    }
  }, [user]);

  useEffect(() => {
    async function getParameters() {
      try {
        const { data } = await http.get("/ccf-parameters/list");
        setParameters(data);
        setDefaults(data, undefined, undefined, undefined);
      }
      catch {
        //ignore error
      }
    }

    getParameters();
  }, [user.SITE]);

  useEffect(() => {
    async function getOperations() {
      if (user !== '') {
        try {
          const siteQuery = user.SITE !== "CO" ? `?site=${user.SITE}` : ``;
          const { data } = await http.get(`/operations/live${siteQuery}`);
          setOperations(data);
          setDefaults(undefined, data, undefined, undefined);
        }
        catch {
          //ignore error
        }
      }
    }

    async function getCcfOperations() {
      if (user !== '') {
        try {
          const siteQuery = user.SITE !== "CO" ? `?site=${user.SITE}` : ``;
          const { data } = await http.get(`/operations/recent${siteQuery}`);
          setCcfOperations(data);
        }
        catch {
          //ignore error
        }
      }
    }
    
    getOperations();
    getCcfOperations();
  }, [user, user.SITE]);

  useEffect(() => {
    async function getTypes() {
      try {
        const { data } = await http.get("/ccf-comment-types");
        setCommentTypes(data);
        setDefaults(undefined, undefined, data, undefined);
      }
      catch {
        //ignore error
      }
    }
    
    getTypes();
  }, []);
  
  const setDefaults = (parameters, operations, commentTypes, comment) => {
    if (parameters && parameters.length === 1) setParameter(parameters[0].CODE);
    else setParameter("");

    if (operations && operations.length === 1) setOperation(operations[0].CODE);
    else setOperation("");

    if (commentTypes && commentTypes.length === 1) setCommentType(commentTypes[0].id);
    else setCommentType("");

    if (comment) setComment(comment);
}

  const handleRaise = () => {
    setShowModal(true);
  };

  const handleChange = ({ currentTarget: input }) => {
    const value = input.type === "checkbox" ? input.checked : input.value;
    switch (input.name) {
      case 'parameter':
        setParameter(value);
        break;
      case 'operation':
        setOperation(value);
        break;
      case 'commentType':
        setCommentType(Number(value));
        break;
      case 'comment':
        setComment(value);
        break;
      case 'showClosed':
        setShowClosedCCFs(value);
        break;
      default:
        //do nothing
        break;
    }
  };

  const handleHide = () => {
    setDefaults(parameters, operations, commentTypes, "");
    setShowModal(false);
  }

  const submitCCF = async (e) => {
    e.preventDefault();

    let ccfType = '';
    let ccfAnalysis = '';
    const status = 'O';
    const raisedFrom = 'OPERATOR'; //OPERATOR = from CCFList screen, DATAENTRY = from Data Entry screens

    var param = parameters.find(({ CODE }) => CODE === parameter);
    if (param) {
      ccfType = param.CCF_TYPE;
      ccfAnalysis = param.CCF_ANALYSIS;
    }

    try {
      const update = props ? props.updateSidebar : undefined;
      
      const { data: newCcf } = await http.post("/ccf", { parameter, ccfType, ccfAnalysis, operation, status, raisedFrom });

      if (commentType && commentType !== '' && comment && comment !== '') {
        await http.post(`/ccf-comments/${newCcf.id}`, { type: commentType, comment: comment });
      }

      setCcfs((c) => [newCcf, ...c]);
      setShowModal(false);
      setDefaults(parameters, operations, commentTypes, "");
      update();

      window.location =`/ccf/${newCcf.id}/${newCcf.parameter}`;
    } catch (error) {
      if (error.response && error.response.status < 500) {
        toast.error(error.response.data);
      }
    }
  };

  const handleDeleteCCF = async (ccfToDelete) => {
    try {
      const { data } = await http.get(`/ccf/commentcount/${ccfToDelete.id}`);
      setCommentCount(data[0][""]);
      setDeleteItem(ccfToDelete);
      setDeleteHeader("Delete CCF");
      setShowDeleteModal(true);    
    }
    catch {
      //ignore error
    }
  }

  const deleteClose = async (response) => {
    if (response === "Yes") {
      await applyDeleteCCF(deleteItem);
    }

    setShowDeleteModal(false);
  };
  
  const applyDeleteCCF = async (ccf) => {
    const currentCCFs = [...ccfs];

    try {
      await http.delete(`ccf/${ccf.id}`);

      setCcfs(currentCCFs.filter((f) => f.id !== ccf.id));
      toast.success(`CCF for ${deleteItem.parameter} removed`);
    } catch (error) {
      setCcfs(currentCCFs);
      if (error.response && error.response.status < 500) {
        toast.error(error.response.data);
      }
    }
  };

  let filtered = showClosedCCFs ? ccfs : ccfs.filter((c) => c.status.code !== 'C');
  const deleteName = deleteItem ? `\n${deleteItem.parameter} (${deleteItem.type.name})` : "";
  const deleteComments = commentCount > 0
    ? commentCount === 1
      ? `\n\nThis CCF has 1 comment.`
      : `\n\nThis CCF has ${commentCount} comments.` : "";

  return (
    <>
      <div className="col px-5 pt-2">
        <div className="row">
          <div className="col col-6">
            <button className="btn btn-primary" onClick={handleRaise}>
              Raise CCF
            </button>
          </div>
          <div className="col col-6" style={{textAlign: 'right'}}>
            <Checkbox
              name="showClosed"
              label="Show Closed"
              checked={showClosedCCFs}
              onChange={handleChange}
            />          
          </div>
        </div>
        <div className="row">
          <table className="table">
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Raised By</th>
                <th>Raised From</th>
                <th>Type</th>
                <th>Analysis</th>
                <th>Raised</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((ccf) => (
                <tr key={ccf.id}>
                  <td>
                    <Link to={`/ccf/${ccf.id}/${ccf.parameter}`}>
                      <button className="btn btn-link btn-left-align">
                        {ccf.parameter}
                      </button>
                    </Link>
                  </td>
                  <td>{ccf.raised_by}</td>
                  <td>{ccf.raised_from}</td>
                  <td>{ccf.type.name}</td>
                  <td>{ccf.analysis.name}</td>
                  <td>{moment.utc(ccf.raised_at).calendar()}</td>
                  <td>
                    <span
                      className={
                        ccf.status.code === "O"
                          ? "badge badge-warning"
                          : ccf.status.code === 'R'
                            ? "badge badge-primary"
                            : "badge badge-success"
                      }
                    >
                      {ccf.status.name}
                    </span>
                  </td>
                  <td style={{width: 64}}>
                    { ccf.status.code === 'C' && user.MAINTAIN_CCF
                      ? <button className="btn btn-sm btn-danger" onClick={() => handleDeleteCCF(ccf)}>
                          <i className="fas fa-trash" />
                        </button>
                      : <div/> }
                    </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <CCFModal
        show={showModal}
        parameters={parameters}
        parameter={parameter}
        operations={ccfOperations}
        operation={operation}
        commentTypes={commentTypes.filter((f) => f.id !== 6)}
        commentType={commentType}
        comment={comment}
        onChange={handleChange}
        onSubmit={submitCCF}
        onHide={handleHide}
        siteQuery={user.SITE !== "CO" ? `?site=${user.SITE}` : ``}
      />
      <DeleteModal
        show={showDeleteModal}
        handleClose={deleteClose}
        header={deleteHeader}
        description={deleteName}
        comments={deleteComments}
      />
    </>
  );
};

const CCFModal = ({ show, parameters, parameter, operations, operation, commentTypes, commentType, comment, onChange, onSubmit, onHide }) => {
  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton className="bg-warning">
        <Modal.Title>Raise New CCF</Modal.Title>
      </Modal.Header>
      <form>
        <Modal.Body>
          <Select
            name="parameter"
            label="Parameter"
            placeholder="Select Parameter"
            value={parameter}
            options={parameters}
            listKey="CODE"
            listName="CCF_TITLE"
            onChange={onChange}
          />
          <Select
            name="operation"
            label="Operation"
            placeholder="Select Operation"
            value = {operation}
            options={operations}
            listKey="CODE"
            listName="COMMENTS"
            onChange={onChange}
          />
          <Select
            name="commentType"
            label="Heading"
            placeholder="Select Heading"
            value={commentType}
            options={commentTypes}
            onChange={onChange}
            disabled={false}
          />
          <textarea
            className="form-control"
            cols={60}
            rows={6}
            name="comment"
            label="Comment"
            placeholder="Comment"
            value={comment}
            onChange={onChange}
          />
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-primary" onClick={onSubmit}>
            Submit
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default CCFList;
